import { paramsToMeili, hitsToSharetribe } from './meilisearch';
import { searchListingsIndex } from './meilisearchClient';
import * as log from './log';

const PRIORITY_SORT_KEYS = {
  listings: ['meta_isBoosted'],
};

const BOOST_OVERLAY_MAX_PER_PAGE = 100;
const META_PREFIX = 'meta_';
const SEARCH_BACKEND_ENV = 'REACT_APP_SEARCH_BACKEND';
const SEARCH_BACKEND_MEILISEARCH = 'meilisearch';

const isAscending = key => key.startsWith('-');
const stripDirection = key => (isAscending(key) ? key.slice(1) : key);

const isMeilisearchBackend = () =>
  process.env[SEARCH_BACKEND_ENV] === SEARCH_BACKEND_MEILISEARCH;

const composeSort = (priorityKeys, sortValue) => {
  const existing =
    typeof sortValue === 'string' && sortValue.length > 0
      ? sortValue.split(',').map(s => s.trim()).filter(Boolean)
      : [];
  const priorityBareKeys = new Set(priorityKeys.map(stripDirection));
  const filtered = existing.filter(s => !priorityBareKeys.has(stripDirection(s)));
  return [...priorityKeys, ...filtered].join(',');
};

const buildOverlayFilters = priorityKeys =>
  priorityKeys.reduce((acc, key) => {
    const metaKey = stripDirection(key);
    if (metaKey.startsWith(META_PREFIX)) {
      acc[metaKey] = '1,';
    }
    return acc;
  }, {});

const mergeIncluded = (mainIncluded = [], overlayIncluded = []) => {
  const seen = new Map();
  for (const inc of [...mainIncluded, ...overlayIncluded]) {
    const key = `${inc?.type}:${inc?.id?.uuid}`;
    if (!seen.has(key)) seen.set(key, inc);
  }
  return Array.from(seen.values());
};

const mergeOverlayOntoMain = (overlayResp, mainResp, perPage) => {
  const overlayItems = overlayResp?.data?.data ?? [];
  const mainItems = mainResp?.data?.data ?? [];
  if (overlayItems.length === 0) return mainResp;

  const overlayIds = new Set(overlayItems.map(it => it?.id?.uuid));
  const dedupedMain = mainItems.filter(it => !overlayIds.has(it?.id?.uuid));
  const merged = [...overlayItems, ...dedupedMain].slice(0, perPage);

  return {
    ...mainResp,
    data: {
      ...mainResp.data,
      data: merged,
      included: mergeIncluded(mainResp?.data?.included, overlayResp?.data?.included),
    },
  };
};

const queryWithBoostOverlay = (originalQuery, params, rest, priorityKeys) => {
  const page = params?.page ?? 1;
  if (page !== 1) {
    return originalQuery(params, ...rest);
  }
  const perPage = params?.perPage ?? 24;
  const overlayParams = {
    ...params,
    ...buildOverlayFilters(priorityKeys),
    page: 1,
    perPage: BOOST_OVERLAY_MAX_PER_PAGE,
  };
  return Promise.all([
    originalQuery(overlayParams, ...rest),
    originalQuery(params, ...rest),
  ]).then(([overlayResp, mainResp]) =>
    mergeOverlayOntoMain(overlayResp, mainResp, perPage)
  );
};

// Match the message Meili returns when a `sort` token references an attribute
// missing from the index's sortableAttributes — e.g.
// "Attribute `isSpotlight` is not sortable.". We rescue the query by stripping
// the offending token(s) and retrying once so the section still renders.
const UNSORTABLE_ATTR_RX = /Attribute `([^`]+)` is not sortable/g;

const extractUnsortableAttrs = err => {
  const msg = err?.body?.message || err?.message || '';
  const attrs = [];
  let m;
  while ((m = UNSORTABLE_ATTR_RX.exec(msg)) !== null) attrs.push(m[1]);
  return attrs;
};

const stripSortTokensForAttrs = (params, attrs) => {
  if (typeof params?.sort !== 'string' || !params.sort || attrs.length === 0) return params;
  const drop = new Set(attrs);
  const kept = params.sort
    .split(',')
    .map(s => s.trim())
    .filter(Boolean)
    .filter(token => {
      const bare = token.replace(/^-/, '');
      const attr = bare.startsWith('meta_') ? bare.slice('meta_'.length) : bare;
      return !drop.has(attr);
    });
  return { ...params, sort: kept.length ? kept.join(',') : undefined };
};

const delegateToMeilisearch = async params => {
  const safeParams = params || {};
  try {
    const meiliResp = await searchListingsIndex(paramsToMeili(safeParams));
    return hitsToSharetribe(meiliResp);
  } catch (err) {
    const badAttrs = extractUnsortableAttrs(err);
    if (badAttrs.length === 0) {
      // Unrecoverable Meili error. The allow-lists in paramsToMeili should keep
      // filter/sort clauses valid, so a 4xx here means the FE lists have drifted
      // from the index settings. Capture the offending body so it names the attr.
      if (err?.code === 'MEILISEARCH_UPSTREAM') {
        log.error(err, 'meilisearch-search-rejected', {
          upstreamStatus: err.upstreamStatus,
          meiliCode: err.meiliCode,
          filter: err.requestBody?.filter,
          sort: err.requestBody?.sort,
          q: err.requestBody?.q,
        });
      }
      throw err;
    }
    const retryParams = stripSortTokensForAttrs(safeParams, badAttrs);
    const meiliResp = await searchListingsIndex(paramsToMeili(retryParams));
    return hitsToSharetribe(meiliResp);
  }
};

const wrapNamespaceQuery = (namespace, namespaceName, priorityKeys) => {
  if (!namespace?.query || !priorityKeys?.length) return;
  const originalQuery = namespace.query.bind(namespace);
  namespace.query = (params, ...rest) => {
    if (namespaceName === 'listings' && isMeilisearchBackend()) {
      return delegateToMeilisearch(params);
    }
    const hasKeywords = !!params?.keywords;
    if (hasKeywords) {
      return queryWithBoostOverlay(originalQuery, params, rest, priorityKeys);
    }
    return originalQuery(
      { ...(params || {}), sort: composeSort(priorityKeys, params?.sort) },
      ...rest
    );
  };
};

/**
 * Install SDK-level query interceptors that must run for every caller.
 * The seam where the frontend asserts non-negotiable query shape.
 *
 * Two strategies, picked per call:
 *  - server-side `sort=` injection prepends priority keys when no `keywords`
 *    are present (covers Newest, Price asc/desc, default sort)
 *  - boost-overlay merge when `keywords` are present (relevance mode)
 *
 * When `REACT_APP_SEARCH_BACKEND === 'meilisearch'` the `listings.query`
 * call is delegated direct from the browser to Meilisearch via
 * {@link searchListingsIndex} + {@link paramsToMeili} + {@link hitsToSharetribe},
 * bypassing both the sort-prepend and the keyword-overlay paths because
 * Meili's `customRanking` handles boost-on-top natively.
 *
 * Extend `PRIORITY_SORT_KEYS` to forcibly prepend sort tokens to other
 * namespaces (`messages`, `transactions`, etc.) without touching call sites.
 *
 * @param {Object} sdk Sharetribe SDK instance from `createInstance`
 * @returns {Object} the same sdk, with the configured `*.query` methods wrapped
 */
export const applySdkInterceptors = sdk => {
  if (!sdk) return sdk;
  for (const [namespaceName, priorityKeys] of Object.entries(PRIORITY_SORT_KEYS)) {
    wrapNamespaceQuery(sdk[namespaceName], namespaceName, priorityKeys);
  }
  return sdk;
};
